if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }

      if (this.zoomInitHandler) {
        window.removeEventListener('on:debounced-resize', this.zoomInitHandler);
      }
    }

    init() {
      this.section = this.closest('.js-product');
      this.noSelectedVariant = this.hasAttribute('data-no-selected-variant');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');

      // LBRGT: stacked layout with full-width lead media (main-product setting stacked_first_large)
      this.lbrgtFirstLarge = this.dataset.layout === 'stacked' && !!this.closest('.product-media--first-large');
      this.lbrgtOrder = Array.from(this.querySelectorAll('.media-viewer__item'));
      this.lbrgtNav = this.querySelector('.lbrgt-lead-nav');
      if (this.lbrgtNav) {
        this.lbrgtNav.addEventListener('click', (evt) => {
          const btn = evt.target.closest('[data-lbrgt-step]');
          if (!btn) return;
          evt.preventDefault();
          evt.stopPropagation();
          this.lbrgtStep(Number(btn.dataset.lbrgtStep));
        });
        // The arrows sit in the lead item on desktop but over the slider on phones. After a colour
        // change on desktop they stayed inside that colour's slide when the window got narrow (or
        // a tablet was turned), so only that one colour showed arrows (22.10.). Each side of the
        // breakpoint puts them back where they belong.
        this.lbrgtNavHome = this.lbrgtNav.parentElement;
        window.addEventListener('on:breakpoint-change', () => {
          if (!this.lbrgtFirstLarge) return;
          const home = theme.mediaMatches.md
            ? this.querySelector('.media-viewer__item--lead')
            : this.lbrgtNavHome;
          if (home && this.lbrgtNav.parentElement !== home) home.appendChild(this.lbrgtNav);
        });
      }

      // LBRGT: "Show more" cut of the first-large gallery (setting lbrgt_gallery_more, 22.10.). The
      // closed state comes from Liquid. Bound whatever the viewport: a window widened past 769px
      // after loading on a phone width still gets a working button.
      this.lbrgtMoreLimit = Number(this.dataset.lbrgtMore) || 0;
      this.lbrgtMoreOpen = false;
      this.lbrgtMoreBtn = this.querySelector('.lbrgt-gallery-more__btn');
      if (this.lbrgtMoreLimit && this.lbrgtMoreBtn) {
        this.lbrgtMoreBtn.addEventListener('click', () => this.lbrgtMoreToggle());
      }

      if (this.hasAttribute('data-zoom-enabled')) {
        this.galleryModal = this.querySelector('.js-media-zoom-template').content.firstElementChild.cloneNode(true);
      }

      if (this.mediaGroupingEnabled && !this.noSelectedVariant) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        // LBRGT: the first-large layout stays stacked above md, so a resize there has nothing to
        // re-initialise. Re-running initGallery pulled the load-time variant's image back into the
        // lead slot and scrolled the page up to the gallery on every resize - on a tablet that is
        // every time the browser bar slides in or out while scrolling.
        this.resizeInitHandler = this.resizeInitHandler || (() => {
          if (this.lbrgtFirstLarge && theme.mediaMatches.md) return;
          this.initGallery();
        });
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
      } else {
        this.initGallery();
      }

      if (this.zoomLinks) {
        if (this.dataset.zoomTrigger === 'hover') {
          this.triggerZoomInit();
        } else {
          this.zoomLinks.forEach((zoomLink) => {
            zoomLink.addEventListener('click', (evt) => {
              this.triggerZoomInit();
              evt.preventDefault();
            });
          });
        }
      }

      this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
    }

    triggerZoomInit() {
      this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
      this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
      window.addEventListener('on:debounced-resize', this.zoomInitHandler);
      this.initZoom();
      this.zoomLinks.forEach((zoomLink) => {
        zoomLink.addEventListener('click', (evt) => {
          evt.preventDefault();
        });
      });
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        const variantMedia = this.viewer.querySelector(
          `[data-media-id="${evt.detail.variant.featured_media.id}"]`
        );
        this.customSetActiveMedia(variantMedia, true);
      }
    }

    /**
     * Gets the media group from currently selected variant options.
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
        if (selector.dataset.selectorType === 'dropdown') {
          return selector.querySelector('.custom-select__btn').textContent.trim();
        }
        return selector.querySelector('input:checked').value;
      }
      return null;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    /**
     * Gets an object mapping media to groups, and the reverse
     * @returns {?object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {}
        };

        // set up grouping
        const variantMediaData = this.getMediaGroupData();
        let currentMediaOptionName = false;
        this.viewerItems = this.querySelectorAll('.media-viewer__item');
        this.viewerItems.forEach((item) => {
          for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
            if (parseInt(item.dataset.mediaId, 10) === variantMediaData.variantMedia[i].mediaId) {
              if (currentMediaOptionName !== variantMediaData.variantMedia[i].option) {
                currentMediaOptionName = variantMediaData.variantMedia[i].option;
              }
            }
          }
          if (currentMediaOptionName) {
            if (!this.mediaGroupMap.groups[currentMediaOptionName]) {
              this.mediaGroupMap.groups[currentMediaOptionName] = {
                name: currentMediaOptionName,
                items: []
              };
            }
            const groupItem = { main: item };
            if (this.thumbs) {
              groupItem.thumb = this.thumbs.querySelector(
                `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
              );
            }
            this.mediaGroupMap.groups[currentMediaOptionName].items.push(groupItem);
          }
        });

        // add helper
        this.mediaGroupMap.groupFromItem = (item) => {
          const groups = Object.keys(this.mediaGroupMap.groups);
          for (let i = 0; i < groups; i += 1) {
            const group = groups[i];
            for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
              if (this.mediaGroupMap.groups[group].items[j] === item) {
                return this.mediaGroupMap.groups[group];
              }
            }
          }
          return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Show only images associated to the current variant
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName) {
      const mediaGroupMap = this.getMediaGroupMap();
      const selectedGroup = groupName
        ? mediaGroupMap.groups[groupName]
        : mediaGroupMap.groupFromItem(this.currentItem);

      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;
          this.viewerItems.forEach((item) => {
            item.style.display = 'none';
            item.classList.remove('media-viewer__item--single');
          });
          this.thumbsItems.forEach((item) => {
            item.style.display = 'none';
          });

          let currentItemIsVisible = false;
          selectedGroup.items.forEach((item) => {
            item.main.style.display = '';
            if (item.thumb) {
              item.thumb.style.display = '';
            }
            if (item.main === this.currentItem) {
              currentItemIsVisible = true;
            }
          });
          // LBRGT: re-cut for the media of the new group (no-op without the "Show more" setting)
          this.lbrgtMoreApply();
          this.setVisibleItems();

          // If current item is not in this group, set it as the active item
          if (!currentItemIsVisible) {
            this.customSetActiveMedia(selectedGroup.items[0].main, true);
          }

          // Handle single images on stacked view
          if (selectedGroup.items.length === 1) {
            selectedGroup.items[0].main.classList.add('media-viewer__item--single');
          }
        }
      } else {
        this.viewerItems.forEach((item) => {
          item.style.display = '';
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = '';
        });
        // LBRGT: all media are back, so the "show more" cut is counted anew
        if (this.lbrgtMoreLimit) {
          this.lbrgtMoreApply();
          this.setVisibleItems();
        }
      }
    }

    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();
      if (this.visibleItems.length <= 1) return;

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentItem = this.visibleItems[this.currentIndex];
      this.addListeners();

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }

      if (!this.isFeatured && document.hasFocus()) {
        // Eager load the slider images for smooth UX
        this.viewer.querySelectorAll('.product-image[loading="lazy"]').forEach((img, index) => {
          setTimeout(() => {
            img.loading = 'eager';
          }, 500 * (index + 1));
        });
      }

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      this.customSetActiveMedia(currentItem, true);
    }

    addListeners() {
      this.viewer.addEventListener('scroll', this.handleScroll.bind(this));
      if (this.controls) this.controls.addEventListener('click', this.handleNavClick.bind(this));
      if (this.thumbs) this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Initialized the zoom on hover for desktop
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        const zoomWidth = Number(el.querySelector('.zoom-image').dataset.originalWidth || 0);
        const imageWidth = el.querySelector('.product-image').getBoundingClientRect().width;
        if (theme.mediaMatches.md && ((zoomWidth - 75) > (imageWidth))) {
          el.addEventListener('mousemove', this.zoomEventListener);
          el.classList.remove('pointer-events-none');
        } else {
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.classList.add('pointer-events-none');
        }
      });
    }

    /**
     * Handles mouse move over a zoomable image
     * @param {?object} evt - Event object.
     */
    handleZoomMouseMove(evt) {
      if (this.hasAttribute('data-zoom-enabled') && !this.dataset.zoomTrigger && !evt.target.closest('.gallery-zoom-modal')) {
        return;
      }

      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      try {
        const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
        const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        // Fail silently
      }
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (!evt.target.matches('.media-ctrl__btn')) return;

      const itemToShow = evt.target === this.nextBtn
        ? this.visibleItems[this.currentIndex + 1]
        : this.visibleItems[this.currentIndex - 1];

      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });
    }

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumb = evt.target.closest('[data-media-id]');
      if (!thumb) return;

      const itemToShow = this.querySelector(`[data-media-id="${thumb.dataset.mediaId}"]`);
      this.customSetActiveMedia(itemToShow);

      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      // LBRGT: the "show more" cut only applies from 769px - below it every slide is visible
      // again, so the list has to be counted anew (it kept 7 and swiping past slide 7 threw)
      if (this.lbrgtMoreLimit) this.setVisibleItems();
      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Stub for variant-picker calls. Listening to change event instead.
     */
    // eslint-disable-next-line class-methods-use-this
    setActiveMedia() {}

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (mediaItem === this.currentItem) return;
      window.pauseAllMedia(this);
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        // Update the active class and scroll to the active media
        if (this.stackedUnderline) {
          if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');
          mediaItem.classList.add('is-active');
          this.previousMediaItem = mediaItem;
        }

        // LBRGT: show the media in the full-width lead slot instead of scrolling to it
        if (this.lbrgtFirstLarge) {
          this.lbrgtPromote(mediaItem);
          return;
        }

        if (this.stackedScroll !== 'never') {
          const y = mediaItem.getBoundingClientRect().top
            + document.documentElement.scrollTop - 150;

          // If the element is far enough away to scroll to it
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'smooth'
            });
          }
        }
        return;
      }

      if (scrollToItem) this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
      if (this.thumbs) this.setActiveThumb();

      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex === 0;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
        }

        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    /**
     * Sets the active thumbnail.
     */
    /**
     * LBRGT: moves a media item into the full-width lead slot, the rest keep their original order.
     * @param {Element} mediaItem - Media item to show large.
     */
    lbrgtPromote(mediaItem) {
      if (!mediaItem) return;
      // Moving the arrows to the new lead takes them out of the DOM for a moment, which drops
      // keyboard focus to <body> - a second Enter did nothing (verification 22.10.)
      const refocus = this.lbrgtNav && this.lbrgtNav.contains(document.activeElement) ? document.activeElement : null;
      this.viewer.prepend(mediaItem);
      this.lbrgtOrder.forEach((el) => {
        if (el !== mediaItem) this.viewer.appendChild(el);
      });
      if (this.lbrgtNav) mediaItem.appendChild(this.lbrgtNav);
      // "Show more" cut in the new order: a colour photo from beyond the limit stays visible as
      // the lead and the next media fill the rows below it
      this.lbrgtMoreApply();
      this.setVisibleItems();
      if (this.lbrgtNav) this.lbrgtNav.hidden = this.visibleItems.length < 2;
      // only now: the arrows show inside the item that carries the lead class
      if (refocus) refocus.focus({ preventScroll: true });

      // No scrolling to the gallery here: a colour change (also on shirt 1 inside a Kaching
      // set) pulled the page ~700px up and away from the buy box; live stays put (QA 21.10.).
    }

    /**
     * LBRGT: shows the previous/next visible media in the lead slot (wraps around).
     * @param {number} dir - -1 for previous, 1 for next.
     */
    lbrgtStep(dir) {
      // media behind "Show more" count too: the arrows page through all of them, the target is
      // promoted to the lead and so becomes visible
      const visible = this.lbrgtOrder.filter((el) => el.clientWidth > 0
        || (el.classList.contains('lbrgt-more-hidden') && el.style.display !== 'none'));
      if (visible.length < 2) return;
      const index = Math.max(visible.indexOf(this.currentItem), 0);
      this.customSetActiveMedia(visible[(index + dir + visible.length) % visible.length], true);
    }

    /**
     * LBRGT: hides the media beyond the "Show more" limit while the gallery is closed. Counts in the
     * current DOM order, so after a colour change the promoted lead and the media after it show.
     */
    lbrgtMoreApply() {
      if (!this.lbrgtMoreLimit) return;
      const items = Array.from(this.viewer.children).filter((el) => el.classList.contains('media-viewer__item'));
      // media hidden by the variant grouping (inline display: none) do not count
      const counted = items.filter((el) => el.style.display !== 'none');
      const needsCut = counted.length - this.lbrgtMoreLimit >= 2;
      const cut = needsCut && !this.lbrgtMoreOpen;
      items.forEach((el) => {
        el.classList.toggle('lbrgt-more-hidden', cut && counted.indexOf(el) >= this.lbrgtMoreLimit);
      });
      if (this.lbrgtMoreBtn) {
        this.lbrgtMoreBtn.parentElement.hidden = !needsCut
          || (this.lbrgtMoreOpen && !this.lbrgtMoreBtn.dataset.labelLess);
      }
    }

    /**
     * LBRGT: opens or closes the "Show more" cut without moving the page.
     */
    lbrgtMoreToggle() {
      const btn = this.lbrgtMoreBtn;
      const y = window.scrollY;
      const top = btn.getBoundingClientRect().top;
      const less = btn.dataset.labelLess;
      this.lbrgtMoreOpen = !this.lbrgtMoreOpen;
      // a video that gets hidden must not keep playing
      if (!this.lbrgtMoreOpen) window.pauseAllMedia(this);
      this.lbrgtMoreApply();
      this.setVisibleItems();
      btn.setAttribute('aria-expanded', String(this.lbrgtMoreOpen));
      btn.querySelector('.lbrgt-gallery-more__label').textContent = this.lbrgtMoreOpen && less
        ? less
        : btn.dataset.labelMore;
      // two-argument scrollTo/scrollBy: instant in every browser (html has no smooth scrolling)
      if (this.lbrgtMoreOpen) {
        // without an "open" text the button is gone - keep focus in the gallery, not on <body>
        if (!less) this.viewer.focus({ preventScroll: true });
        // opening only adds height below the button; Chrome keeps scrollY, this guards the rest
        if (window.scrollY !== y) window.scrollTo(window.scrollX, y);
      } else {
        // closing removes height above the button: without this the page kept its scrollY and the
        // button ended up ~2200px above the viewport, inside the sections below (prototype 22.10.)
        const delta = btn.getBoundingClientRect().top - top;
        if (Math.abs(delta) > 1) window.scrollBy(0, delta);
      }
    }

    setActiveThumb() {
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`
      );
      const btn = this.currentThumb.querySelector('button');

      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);
    }

    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.visibleItems = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);
      // LBRGT: mark the first visible item so stacked layouts can show it full width
      if (this.visibleItems.length) {
        this.viewerItems.forEach((el) => {
          el.classList.toggle('media-viewer__item--lead', el === this.visibleItems[0]);
        });
      }
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb) {
      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;

      if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
        this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
      }
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  customElements.define('media-gallery', MediaGallery);
}
